import { useTranslation } from '@jobcost-monorepo/i18n';
import { Icon, Input } from '@jobcost-monorepo/ui';
import { cn } from '@jobcost-monorepo/utils';
import { ReactElement, useEffect, useState } from 'react';
import { IBaseModel, IListFetchParams, useListContext } from '../../../common';
import React from 'react';

const GridFooter = <
  T extends IBaseModel,
  Q extends IListFetchParams,
>(): ReactElement => {
  const { fetchDataList, dataList, params, goToPage } = useListContext<T, Q>();
  const { t } = useTranslation();
  const { skip, take } = params || { skip: 0, take: 10 };
  const totalPages = Math.ceil(dataList.totalCount / Number(take));
  const isPrevPageDisabled = Number(skip) === 0;
  const isNextPageDisabled = Number(skip) + Number(take) >= dataList.totalCount;
  const totalCount = dataList.totalCount;
  const [pageNumber, setPageNumber] = useState<string>(
    String(Math.ceil(Number(skip) / Number(take)) + 1),
  );

  const goToNextPage = () => {
    if (isNextPageDisabled) return;
    fetchDataList({
      ...params,
      skip:
        Number(skip) + Number(take) <= dataList.totalCount
          ? Number(skip) + Number(take)
          : 0,
    } as Q);
  };

  const goToPrevPage = () => {
    if (isPrevPageDisabled) return;
    fetchDataList({
      ...params,
      skip: Number(skip) >= Number(take) ? Number(skip) - Number(take) : 0,
    } as Q);
  };

  const refreshPage = () => {
    fetchDataList({ ...params } as Q);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const page = Number(pageNumber) > totalPages ? totalPages : pageNumber;
      setPageNumber(String(page));
      if (goToPage) return goToPage(Number(page));
    }
  };

  const handleBlurInput = () => {
    const page = Number(pageNumber) > totalPages ? totalPages : pageNumber;
    setPageNumber(String(page));
    if (goToPage) return goToPage(Number(page));
  };

  const getRowsPaginationFooter = () => {
    if (!totalCount || totalCount === 0) {
      return '';
    }
    const skipTmp = skip ?? 0;
    const takeTmp = take ?? 0;
    const start = `${skipTmp + 1}`;
    const end =
      skipTmp + takeTmp > totalCount ? `${totalCount}` : `${skipTmp + takeTmp}`;
    return `[${start}-${end}] ${t('grid.of', { page: totalCount })}`;
  };

  useEffect(() => {
    setPageNumber(String(Math.ceil(Number(skip) / Number(take)) + 1));
  }, [skip, take]);

  return (
    <div className="flex flex-row justify-between items-center mt-2">
      <div className="flex flex-row items-center space-x-4">
        <p>{getRowsPaginationFooter()}</p>
      </div>

      <div className="flex flex-row justify-end items-center space-x-4">
        <div className="flex flex-row items-center justify-center">
          <p className="hidden md:flex">{t('grid.page')}</p>

          <Input
            value={pageNumber}
            type="number"
            onKeyDown={handleKeyPress}
            onChange={(e) => setPageNumber(e.target.value)}
            onBlur={handleBlurInput}
            className="w-10 h-7 px-2 mx-2"
            width="w-fit"
            disabled={!goToPage}
          />

          <p>{t('grid.of', { page: totalPages > 0 ? totalPages : 1 })}</p>
        </div>

        <div className="flex space-x-2">
          <div
            className={cn(
              'flex size-8 rounded-md bg-pink text-white justify-center items-center cursor-pointer hover:opacity-50 dark:hover:opacity-100  dark:hover:bg-slate-800 dark:hover:text-slate-100 ',
              isPrevPageDisabled &&
                'opacity-50 cursor-not-allowed dark:hover:bg-pink dark:hover:opacity-50',
            )}
            onClick={goToPrevPage}
          >
            <Icon iconName="leftArrow" className="fill-white size-3" />
          </div>

          <div
            className={cn(
              'flex justify-center items-center size-8 rounded-md bg-pink text-white  cursor-pointer hover:opacity-50 dark:hover:opacity-100 dark:hover:bg-slate-800 dark:hover:text-slate-100 ',
              isNextPageDisabled &&
                'opacity-50 cursor-not-allowed dark:hover:bg-pink dark:hover:opacity-50',
            )}
            onClick={goToNextPage}
          >
            <Icon iconName="rightArrow" className="fill-white size-3" />
          </div>
          <div
            className={cn(
              'flex justify-center items-center size-8 rounded-md bg-pink text-white  cursor-pointer hover:opacity-50 dark:hover:opacity-100 dark:hover:bg-slate-800 dark:hover:text-slate-100 ',
            )}
            onClick={refreshPage}
          >
            <Icon iconName="reload" className="fill-white size-3" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GridFooter;
